import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { TenantProvider } from "./context/TenantContext";
import { Toaster } from "./components/ui/sonner";
import InstallPrompt from "./components/InstallPrompt";

// Pages
import LoginPage from "./pages/LoginPage";
import TenantLoginPage from "./pages/TenantLoginPage";
import LandingPage from "./pages/LandingPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import PlatformSettingsPage from "./pages/PlatformSettingsPage";
import BuilderSettingsPage from "./pages/BuilderSettingsPage";
import SalesCompanySettingsPage from "./pages/SalesCompanySettingsPage";
import BillingSettingsPage from "./pages/BillingSettingsPage";
import PlanManagementPage from "./pages/PlanManagementPage";
import DashboardPage from "./pages/DashboardPage";
import ConfiguratorLanding from "./pages/ConfiguratorLanding";
import LeadsPage from "./pages/LeadsPage";
import UsersPage from "./pages/UsersPage";
import RolesPage from "./pages/RolesPage";
import AnalysisPage from "./pages/AnalysisPage";
import PricingPage from "./pages/PricingPage";
import ConfiguratorSettingsPage from "./pages/ConfiguratorSettingsPage";
import ImportExportPage from "./pages/ImportExportPage";
// CRM Pages
import CRMDashboardPage from "./pages/CRMDashboardPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerProfilePage from "./pages/CustomerProfilePage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import ProjectsPage from "./pages/ProjectsPage";
import BookingsPage from "./pages/BookingsPage";
import ReportsPage from "./pages/ReportsPage";
import CustomerPortal from "./pages/CustomerPortal";

// Protected Route Component - requires authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Staff Protected Route - blocks customers from accessing admin/CRM pages
const StaffProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isCustomer } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If user is a customer, redirect them to the portal
  if (isCustomer()) {
    return <Navigate to="/portal" replace />;
  }
  
  return children;
};

// Customer Portal Route - only for customers (or staff who want to view it)
const PortalRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Platform Admin Route - only for platform admins
const PlatformAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin-login" replace />;
  }
  
  // Check if user is platform admin
  const isPlatformAdmin = user?.tenant_role === 'platform_admin' || 
                          user?.role_id === 'role_platform_admin' ||
                          user?.role_id === 'role_developer';
  
  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/demo" element={<ConfiguratorLanding />} />
      <Route path="/c/:tenantSlug" element={<ConfiguratorLanding />} />
      <Route path="/c/:tenantSlug/login" element={<TenantLoginPage />} />
      <Route path="/c/:tenantSlug/*" element={<ConfiguratorLanding />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin-login" element={<AdminLoginPage />} />
      
      {/* Platform Admin Routes */}
      <Route path="/platform-admin" element={
        <PlatformAdminRoute>
          <PlatformAdminPage />
        </PlatformAdminRoute>
      } />
      <Route path="/billing-settings" element={
        <StaffProtectedRoute>
          <BillingSettingsPage />
        </StaffProtectedRoute>
      } />
      <Route path="/plan-management" element={
        <PlatformAdminRoute>
          <PlanManagementPage />
        </PlatformAdminRoute>
      } />
      <Route path="/platform-settings" element={
        <PlatformAdminRoute>
          <PlatformSettingsPage />
        </PlatformAdminRoute>
      } />
      <Route path="/builder/settings" element={
        <StaffProtectedRoute>
          <BuilderSettingsPage />
        </StaffProtectedRoute>
      } />
      <Route path="/sales-company/settings" element={
        <StaffProtectedRoute>
          <SalesCompanySettingsPage />
        </StaffProtectedRoute>
      } />
      
      {/* Staff Protected Routes - Customers cannot access */}
      <Route path="/dashboard" element={
        <StaffProtectedRoute>
          <DashboardPage />
        </StaffProtectedRoute>
      } />
      <Route path="/leads" element={
        <StaffProtectedRoute>
          <LeadsPage />
        </StaffProtectedRoute>
      } />
      <Route path="/users" element={
        <StaffProtectedRoute>
          <UsersPage />
        </StaffProtectedRoute>
      } />
      <Route path="/roles" element={
        <StaffProtectedRoute>
          <RolesPage />
        </StaffProtectedRoute>
      } />
      <Route path="/analysis" element={
        <StaffProtectedRoute>
          <AnalysisPage />
        </StaffProtectedRoute>
      } />
      <Route path="/pricing" element={
        <StaffProtectedRoute>
          <PricingPage />
        </StaffProtectedRoute>
      } />
      <Route path="/configurator-settings" element={
        <StaffProtectedRoute>
          <ConfiguratorSettingsPage />
        </StaffProtectedRoute>
      } />
      <Route path="/import-export" element={
        <StaffProtectedRoute>
          <ImportExportPage />
        </StaffProtectedRoute>
      } />
      {/* CRM Routes - Staff Only */}
      <Route path="/crm" element={
        <StaffProtectedRoute>
          <CRMDashboardPage />
        </StaffProtectedRoute>
      } />
      <Route path="/crm/customers" element={
        <StaffProtectedRoute>
          <CustomersPage />
        </StaffProtectedRoute>
      } />
      <Route path="/crm/customers/:customerId" element={
        <StaffProtectedRoute>
          <CustomerProfilePage />
        </StaffProtectedRoute>
      } />
      <Route path="/crm/opportunities" element={
        <StaffProtectedRoute>
          <OpportunitiesPage />
        </StaffProtectedRoute>
      } />
      <Route path="/crm/projects" element={
        <StaffProtectedRoute>
          <ProjectsPage />
        </StaffProtectedRoute>
      } />
      <Route path="/crm/bookings" element={
        <StaffProtectedRoute>
          <BookingsPage />
        </StaffProtectedRoute>
      } />
      <Route path="/crm/reports" element={
        <StaffProtectedRoute>
          <ReportsPage />
        </StaffProtectedRoute>
      } />
      
      {/* Customer Portal Route */}
      <Route path="/portal" element={
        <PortalRoute>
          <CustomerPortal />
        </PortalRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <TenantProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
              <Toaster position="top-right" />
              <InstallPrompt />
            </AuthProvider>
          </BrowserRouter>
        </TenantProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
